import React from 'react';
import { LuStickyNote } from 'react-icons/lu';

import TextAreaField from 'components/common/TextAreaField';
import {
  TASKS_GRID_NOTES_FIELD_LABEL,
  TASKS_GRID_NOTES_PLACEHOLDER,
} from 'constants/uiConstants';
import { TASK_NOTES_MAX_LENGTH } from 'constants/validationConstants';
import type { IInputTask, IInputTaskBase } from 'interfaces/IInputTask';

import { ApplicationTaskFields } from './taskFactory';

interface TaskSheetNotesCellProps {
  task: IInputTask;
  colSpan?: number;
  rowSpan?: number;
  commitTaskField: (
    task: IInputTask,
    patch: Partial<IInputTaskBase & ApplicationTaskFields>
  ) => void;
}

// An application's note in the category sheet (OP-2556), wrapping so a
// sentence or two reads in full — notes matter to our users and shouldn't
// hide behind a click. Marked with a note icon in darker-grey text so it
// reads as an annotation, not another input. Click anywhere in it to edit.
// A row under the products (TaskSheetNotesRow), or on a wide sheet a column
// beside them, spanning the task's rows.
export const TaskSheetNotesCell: React.FC<TaskSheetNotesCellProps> = ({
  task,
  colSpan,
  rowSpan,
  commitTaskField,
}) => (
  <td
    colSpan={colSpan}
    rowSpan={rowSpan}
    aria-label={TASKS_GRID_NOTES_FIELD_LABEL}
    // A click anywhere in the cell starts editing; the field's own rounded
    // border shows on hover and while editing, matching the other fields.
    className="cursor-text border border-gray-medium bg-white p-1 align-top"
    onClick={(event) => {
      if ((event.target as HTMLElement).closest('textarea, input')) return;
      event.currentTarget.querySelector<HTMLElement>('[tabindex]')?.focus();
    }}
  >
    <div className="flex items-start gap-1.5 pl-2 text-base text-gray-dark">
      <LuStickyNote
        size={14}
        aria-hidden="true"
        className="mt-2 flex-shrink-0 text-gray-dark"
      />
      <div className="min-w-0 flex-1 whitespace-pre-wrap break-words">
        <TextAreaField
          inputClassName="w-full rounded"
          // Not a Tab stop: Tab runs along the product values, and the note
          // is reached by clicking it (OP-2556).
          skipTabStop
          value={task.notes}
          placeholder={TASKS_GRID_NOTES_PLACEHOLDER}
          maxLength={TASK_NOTES_MAX_LENGTH}
          onCommit={(notes) => commitTaskField(task, { notes })}
        />
      </div>
    </div>
  </td>
);

interface TaskSheetNotesRowProps {
  task: IInputTask;
  /** The Task cell, when this is the task's first row (collapsed). */
  leadingCell?: React.ReactNode;
  /** How many product columns the note spans. */
  colSpan: number;
  commitTaskField: (
    task: IInputTask,
    patch: Partial<IInputTaskBase & ApplicationTaskFields>
  ) => void;
}

// The note as its own full-width row under the products; clearing it
// removes the row.
const TaskSheetNotesRow: React.FC<TaskSheetNotesRowProps> = ({
  task,
  leadingCell,
  colSpan,
  commitTaskField,
}) => (
  <tr className="bg-white">
    {leadingCell}
    <TaskSheetNotesCell
      task={task}
      colSpan={colSpan}
      commitTaskField={commitTaskField}
    />
  </tr>
);

export default TaskSheetNotesRow;
